import { observer } from "mobx-react-lite";
import { Form as FinalForm, Field } from "react-final-form";
import React, { useContext, useEffect, useState } from "react";

// reactstrap components
import { Alert, Button, FormGroup, Form, Col } from "reactstrap";
import { FORM_ERROR } from "final-form";
import { RootStoreContext } from "../../app/stores/rootStore";
import TextInput from "../common/TextInput";
import {
  combineValidators,
  composeValidators,
  hasLengthBetween,
  isNumeric,
  isRequired,
  matchesField,
} from "revalidate";
import classNames from "classnames";
import {
  IUpdatePinFormValues,
  UpdatePinFormValues,
} from "../../app/models/offline";
import Loader from "../common/Loader";
import { Row } from "reactstrap/lib";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { translationHookOptions } from "../../app/localization/localization";

const validate = combineValidators({
  //currentPin: composeValidators(isRequired, isNumeric, hasLengthBetween(4, 6))("currentPin"),
  newPin: composeValidators(
    isRequired,
    isNumeric,
    hasLengthBetween(4, 6)
  )("newPin"),
  confirmPin: matchesField(
    "newPin",
    ""
  )({ message: "I PIN non corrispondono" }),
});

export const UpdatePINForm: React.FC = () => {
  const rootStore = useContext(RootStoreContext);
  const {
    updatePINToDb,
    loadNativeAppConfFromDb,
    nativeAppConf,
    resetNativeAppConf,
  } = rootStore.offlineStore;
  const { theme } = rootStore.commonStore;
  const [formLoaded, setFormLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [t] = useTranslation(["nativeapp"], translationHookOptions);

  useEffect(() => {
    loadNativeAppConfFromDb().finally(() => setFormLoaded(true));
    return () => {
      resetNativeAppConf();
    };
  }, [loadNativeAppConfFromDb, resetNativeAppConf]);

  const handleDisablePINClick = (currentPin: string) => {
    setErrorMessage("");
    if (
      nativeAppConf &&
      nativeAppConf.requirePin &&
      nativeAppConf.pin !== parseInt(currentPin)
    ) {
      toast.error(t("pin_error"));
    } else {
      updatePINToDb();
    }
  };

  const checkDisablePINClick = (currentPin: string) => {
    if (
      nativeAppConf &&
      (!nativeAppConf.requirePin || (nativeAppConf.requirePin && !currentPin))
    ) {
      return true;
    } else return false;
  };

  if (!formLoaded) return <Loader />;

  return (
    <>
      <FinalForm
        validate={validate}
        initialValues={new UpdatePinFormValues()}
        onSubmit={(values: IUpdatePinFormValues) => {
          setErrorMessage("");
          if (
            nativeAppConf &&
            nativeAppConf.requirePin &&
            nativeAppConf.pin !== parseInt(values.currentPin)
          ) {
            setErrorMessage(t("pin_error"));
            return { [FORM_ERROR]: t("pin_error") };
          }

          if (values.newPin === values.confirmPin) {
            updatePINToDb(parseInt(values.newPin)).catch((error) => {
              setErrorMessage(t("pin_update_error"));
              return {
                [FORM_ERROR]: error,
              };
            });
          }
        }}
        render={({
          handleSubmit,
          submitting,
          submitError,
          invalid,
          pristine,
          dirtySinceLastSubmit,
          touched,
          values,
        }) => (
          <Form onSubmit={handleSubmit} role="form">
            <div className="pl-lg-4">
              {nativeAppConf && nativeAppConf.requirePin ? (
                <FormGroup className="mb-5">
                  <label className="form-control-label" htmlFor="currentPin">
                    {t("current_pin_label")}
                  </label>
                  <Field
                    id="currentPin"
                    name="currentPin"
                    component={TextInput}
                    type="password"
                    className="form-control-alternative w-75"
                    autoComplete="off"
                  />
                </FormGroup>
              ) : (
                <></>
              )}
              <FormGroup>
                <label className="form-control-label" htmlFor="newPin">
                  {t("new_pin_label")}
                </label>
                <Field
                  id="newPin"
                  name="newPin"
                  component={TextInput}
                  type="password"
                  className="form-control-alternative w-75"
                  autoComplete="off"
                />
              </FormGroup>
              <FormGroup>
                <label className="form-control-label" htmlFor="confirmPin">
                  {t("confirm_pin_label")}
                </label>
                <Field
                  id="confirmPin"
                  name="confirmPin"
                  component={TextInput}
                  type="password"
                  className={classNames(
                    "w-75",
                    touched &&
                      touched.confirmNewPwd &&
                      invalid &&
                      !dirtySinceLastSubmit
                      ? "is-invalid"
                      : "form-control-alternative w-75"
                  )}
                  autoComplete="off"
                />
                <div className="invalid-feedback">
                  {t("mismatching_pin_error")}
                </div>
              </FormGroup>
              {submitError && !dirtySinceLastSubmit && errorMessage && (
                <Alert color="danger">{errorMessage}</Alert>
              )}
              <Row>
                <Col>
                  <Button
                    disabled={(invalid && !dirtySinceLastSubmit) || pristine}
                    className={classNames(
                      "my-4 genericButton",
                      `${theme!.buttonColor} ${theme!.buttonBorderColor} ${
                        theme!.buttonTextColor
                      }`
                    )}
                    type="submit"
                  >
                    {t("save_pin")}
                  </Button>
                </Col>
                <Col>
                  <Button
                    disabled={checkDisablePINClick(values.currentPin)}
                    className={classNames(
                      "my-4 genericButton",
                      `${theme!.buttonColor} ${theme!.buttonBorderColor} ${
                        theme!.buttonTextColor
                      }`
                    )}
                    onClick={() => handleDisablePINClick(values.currentPin)}
                  >
                    {t("disable_pin")}
                  </Button>
                </Col>
              </Row>
            </div>
          </Form>
        )}
      />
    </>
  );
};

export default observer(UpdatePINForm);
